import { useRef, useTransition } from "react";
import { FormattedMessage, defineMessage } from "react-intl";
import Suspense from "@/common/Suspense";
import CompetitionEntitySubmissionStatus from "@/components/CompetitionEntitySubmissionStatus";
import CompetitionLeaderboard, {
  CompetitionLeaderboardHandle,
} from "@/components/CompetitionLeaderboard";
import { ActivityNotFound } from "@/components/ActivityNotFound";
import CompetitionStageSelect from "@/components/CompetitionStageSelect";
import SubjectSubscriptionButton from "@/components/SubjectSubscriptionButton";
import { cn } from "@/utils/tailwind";
import { useSearchParams } from "react-router";
import { graphql, Environment } from "react-relay";
import { LoaderArgs } from "@/utils/route";
import { CompetitionLeaderboardPageQuery as CompetitionLeaderboardPageQueryType } from "./__generated__/CompetitionLeaderboardPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
export default function CompetitionLeaderboardPage() {
  const {
    query: { competitionBySlug: competition },
    variables: { stage },
  } = useCompetitionLeaderboardPageQuery();
  const setSearchParams = useSearchParams()[1];
  const [isTransitioning, startTransition] = useTransition();
  const leaderboardRef = useRef<CompetitionLeaderboardHandle | null>(null);
  const entity = competition?.membership?.asEntity;
  if (!competition) {
    return <ActivityNotFound kind="competition" />;
  }
  return (
    <>
      <div className="flex flex-row">
        <h2 className="text-2xl font-bold w-full font-poppins">
          <FormattedMessage defaultMessage="Leaderboard" />
        </h2>
        <div className="inline-flex row gap-2">
          <CompetitionStageSelect
            competition={competition}
            defaultValue={stage}
            disabled={isTransitioning}
            onChange={(stage) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("stage", stage.toString(10));
                return next;
              });
              startTransition(() => {
                leaderboardRef.current?.refetch({ stage });
              });
            }}
          />

          <SubjectSubscriptionButton subject={competition} />
        </div>
      </div>
      <div className="py-4">
        {entity && (
          <CompetitionEntitySubmissionStatus
            competition={competition}
            entity={entity}
            refetchLeaderboard={leaderboardRef.current?.refetch}
          />
        )}
      </div>
      <div
        className={cn(
          "pt-4 pb-8",
          isTransitioning && "opacity-80 pointer-events-none",
        )}
      >
        <Suspense>
          <CompetitionLeaderboard ref={leaderboardRef} stage={competition} />
        </Suspense>
      </div>
    </>
  );
}
const CompetitionLeaderboardPageQuery = graphql`
  query CompetitionLeaderboardPageQuery(
    $owner: String!
    $slug: String!
    $stage: Int
  ) {
    competitionBySlug(slug: $slug) {
      id
      membership {
        id
        asEntity {
          ...CompetitionEntitySubmissionStatusEntityFragment
        }
      }
      title
      slug
      ...CompetitionLeaderboardFragment @arguments(stage: $stage)
      ...CompetitionStageSelectFragment @arguments(hasLeaderboard: true)
      ...CompetitionEntitySubmissionStatusCompetitionFragment
        @arguments(owner: $owner)
      ...SubjectSubscriptionButtonFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { slug: owner, track: slug }, request }: LoaderArgs) => {
    if (!slug || !owner) {
      throw new Response("No slug or owner", { status: 500 });
    }
    const stage = new URL(request.url).searchParams.get("stage");
    return preload<CompetitionLeaderboardPageQueryType>(
      environment,
      CompetitionLeaderboardPageQuery,
      {
        owner,
        slug,
        stage: stage !== null ? parseInt(stage, 10) : null,
      },
    );
  };
};
const useCompetitionLeaderboardPageQuery = () =>
  usePreloaded<CompetitionLeaderboardPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Leaderboard" }),
};
